'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does Kylie Cosmetics Have Microplastics? The Ten Purity Badges"
      author="MicroPlastics Research Desk"
      date="July 31, 2026"
      readTime="10 min read"
      heroImage="/blog/heroes/cosmetics.webp"
      heroImageAlt="Does Kylie Cosmetics have microplastics clean vegan talc free badges ingredient list 2026"
    >
      <BlogLastUpdated iso="2026-07-31" formatted="July 31, 2026" />

      <BlogTLDR>
        <strong>Some products do, and the framing is the story.</strong>{' '}
        Kylie Cosmetics displays <strong>ten purity badges</strong> on
        its own product pages: vegan, cruelty free, clean, talc free,
        gluten free, tear free, paraben free, fragrance free, sulfate
        free, hypoallergenic. When Coty relaunched the brand in 2021 it
        announced formulas that banned{' '}
        <strong>more than 1,600 ingredients</strong>. Not one of those
        ten badges, and nothing in that ban list as announced, is about{' '}
        <strong>plastic</strong>. Of 20 distinct Kylie formulas we
        captured on 30 July 2026, <strong>4 listed a solid synthetic
        polymer</strong>: polyethylene in the{' '}
        <strong>King Kylie Matte Lip Kit</strong> and{' '}
        <strong>High Gloss Liner Duo</strong>, and a silicone elastomer
        crosspolymer in <strong>Power Plush Longwear Foundation</strong>{' '}
        and the Hyaluronic Acid Serum. The good news:{' '}
        <strong>16 of 20 were clean</strong>, including the Matte Liquid
        Lipstick.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Kylie Cosmetics&apos; own site shows <strong>ten purity claims</strong>. Plastic-free is not among them, and no mainstream clean standard includes it.</>,
          <><strong>Kylie Matte Liquid Lipstick is clean</strong> on this test: isododecane, mica, dicalcium phosphate, octyldodecanol, hydrogenated styrene/isoprene copolymer. That copolymer is an elastomeric binder, not a solid particle.</>,
          <><strong>Polyethylene</strong> appears in the King Kylie Matte Lip Kit and the High Gloss Liner Duo, in both cases in the liner component, where it hardens wax.</>,
          <><strong>Power Plush Longwear Foundation</strong> lists a dimethicone/vinyl dimethicone crosspolymer, a solid silicone elastomer. This counts; ordinary liquid dimethicone does not.</>,
          <>&ldquo;Talc free&rdquo; is a badge. &ldquo;Polymer free&rdquo; is not a thing anyone certifies, which is precisely why it is worth checking yourself.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Ten badges, and the one that is missing
      </h2>
      <p>
        Go to a Kylie Cosmetics product page and you are shown a row of
        circular purity icons. As published on kyliecosmetics.com on{' '}
        <strong>30 July 2026</strong>, the set is:
      </p>
      <p className="text-sm bg-[#F7F5F2] p-4 rounded-lg border border-black/5">
        vegan &middot; cruelty free &middot; clean &middot; talc free
        &middot; gluten free &middot; tear free &middot; paraben free
        &middot; fragrance free &middot; sulfate free &middot;
        hypoallergenic
      </p>
      <p>
        In July 2021, Coty announced the relaunch of the brand with
        &ldquo;clean and vegan&rdquo; formulas, stating that the new
        products excluded animal oils, parabens and gluten, and banned a
        list of <strong>over 1,600 other ingredients</strong>.
      </p>
      <p>
        Sixteen hundred is a genuinely large number, and this is not a
        gotcha about Kylie specifically. It is the shape of the entire
        clean-beauty category. <strong>These lists are built around
        toxicological hazard, preservatives, surfactants, allergens,
        endocrine-disruption candidates. Synthetic polymer microparticles
        are not a toxicological hazard in that sense.</strong> They are
        chemically inert. Their problem is persistence, and no clean
        standard is built to catch persistence.
      </p>
      <p>
        So a product can be vegan, cruelty free, clean, talc free,
        paraben free, sulfate free and hypoallergenic and still contain
        polyethylene. That is not hypocrisy; it is a category error that
        the whole industry shares. We wrote about the mechanics of it in{' '}
        <Link href="/blog/clean-beauty-microplastics-loophole" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          clean beauty&apos;s plastic loophole
        </Link>
        .
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What the ingredient lists actually show
      </h2>

      <BlogComparisonTable
        caption="Kylie Cosmetics products whose published ingredient list includes a solid synthetic polymer (checked 30 July 2026)"
        columns={[
          { key: 'product', label: 'Product' },
          { key: 'polymer', label: 'Solid polymer listed' },
          { key: 'role', label: 'What it does' },
        ]}
        rows={[
          { product: 'King Kylie Matte Lip Kit', polymer: 'Polyethylene', role: 'Wax hardener in the liner, listed alongside propyldimethylsiloxysilicate and mica.' },
          { product: 'High Gloss Liner Duo', polymer: 'Polyethylene', role: 'Same, structures the pencil.' },
          { product: 'Power Plush Longwear Foundation', polymer: 'Dimethicone/Vinyl Dimethicone Crosspolymer', role: 'Silicone elastomer for soft-focus texture and wear. A solid swollen particle.' },
          { product: 'Hyaluronic Acid Serum', polymer: 'Dimethicone/Vinyl Dimethicone Crosspolymer', role: 'Texture. Leave-on skincare, so the EU deadline here is Oct 2029, not 2035.' },
        ]}
      />
      <p>
        And the one people ask about most, the{' '}
        <strong>Matte Liquid Lipstick</strong>, as published by Kylie
        Cosmetics:
      </p>
      <p className="text-sm bg-[#F7F5F2] p-4 rounded-lg border border-black/5">
        Isododecane, Mica, Dicalcium Phosphate, Octyldodecanol,
        Hydrogenated Styrene/Isoprene Copolymer, Stearalkonium
        Hectorite, Alcohol, Flavor (Aroma), Water (Aqua),
        Pentaerythrityl Tetra-Di-T-Butyl Hydroxyhydrocinnamate, Tin
        Oxide, Red 7 Lake (CI 15850), Iron Oxides (CI 77491, CI 77492),
        Blue 1 Lake (CI 42090), Titanium Dioxide (CI 77891), Yellow 5
        Lake (CI 19140), Red 28 Lake (CI 45410).
      </p>
      <p>
        <strong>No solid polymer particles.</strong> The hydrogenated
        styrene/isoprene copolymer is a synthetic elastomeric binder, the
        thing that gives a liquid lipstick its flexible, non-cracking
        film. It is not supplied as discrete solid particles, so it does
        not meet the EU definition. Sixteen of the twenty Kylie formulas
        we captured came out this way.
      </p>
      <p className="text-sm text-gray-500 italic">
        We read 20 distinct formulas from kyliecosmetics.com&apos;s own
        product pages. This is a sample of the catalogue rather than a
        census.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Is polyethylene in a lip liner a problem?
      </h2>
      <p>
        Proportionally, this is the mildest finding in our whole audit,
        and it would be dishonest to inflate it.
      </p>
      <p>
        Polyethylene in a pencil is a wax-structure hardener. It is
        present at low concentration, it is bound in a wax matrix rather
        than supplied as free-flowing microbeads, and a lip liner is
        used in milligram quantities. Compared with a loose powder built
        on nylon microspheres, or PET glitter designed to shed, it is a
        minor case.
      </p>
      <p>
        It still meets the definition, and it will still need to change
        for the EU market by <strong>17 October 2035</strong>. But if you
        are prioritising, the pencil is near the bottom of the list. The
        powder is at the top.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The swaps
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Lip liners:</strong> pencils structured on candelilla or carnauba wax instead of polyethylene. The wax type is normally in the first five ingredients.</li>
        <li><strong>Long-wear foundation:</strong> harder. Silicone elastomers are what deliver the blurred, long-wear finish, and the alternatives, more traditional emulsions, wear differently. This is a genuine trade-off, not a free swap.</li>
        <li><strong>If you like the Matte Liquid Lipstick:</strong> keep it. It passes this test.</li>
      </ul>

      <BlogAppCTA
        headline="Check what your 'clean' makeup actually contains"
        sub="Vegan, cruelty free, paraben free and clean say nothing about polymers. Scan any product and the app parses the full ingredient list, flags solid polymer particles separately from liquid silicones, and shows a cleaner same-format swap."
        buttonLabel="Scan a makeup product"
      />

      <p>
        This is one of five brand breakdowns from our{' '}
        <Link href="/blog/microplastics-in-makeup-brands-audited" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          audit of 81 formulas across Fenty, Huda Beauty, rhode and Kylie Cosmetics
        </Link>
        . See also{' '}
        <Link href="/blog/does-fenty-beauty-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          Fenty Beauty
        </Link>
        ,{' '}
        <Link href="/blog/does-huda-beauty-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          Huda Beauty
        </Link>
        , and{' '}
        <Link href="/blog/does-rhode-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          rhode
        </Link>
        .
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>The full INCI list for the exact Kylie SKU and shade from the barcode.</>,
          <>Solid polymer particles (polyethylene, silicone elastomer crosspolymers) flagged separately from liquid silicones and elastomeric binders.</>,
          <>Which marketing claims a product carries, and what each of them does and does not cover.</>,
          <>Rinse-off, leave-on or make-up, which sets the EU deadline.</>,
          <>A cleaner same-format, same-budget alternative.</>,
        ]}
      />

      <BlogFAQ
        items={[
          {
            question: 'Does Kylie Cosmetics contain microplastics?',
            answer:
              'Some products do. Of the 20 distinct Kylie Cosmetics formulas we captured from kyliecosmetics.com on 30 July 2026, four listed a solid synthetic polymer particle: the King Kylie Matte Lip Kit and High Gloss Liner Duo (polyethylene), and Power Plush Longwear Foundation and the Hyaluronic Acid Serum (dimethicone/vinyl dimethicone crosspolymer, a silicone elastomer). Sixteen contained none, including the Matte Liquid Lipstick.',
          },
          {
            question: 'Is Kylie Cosmetics really "clean"?',
            answer:
              'It carries the claim, and Coty stated at the 2021 relaunch that the reformulated products banned more than 1,600 ingredients including parabens, gluten and animal oils. What "clean" does not mean is plastic-free. No mainstream clean-beauty standard excludes synthetic polymer microparticles, because those lists are built around toxicological hazard and polymer particles are chemically inert. A product can be clean, vegan, paraben free and talc free and still contain polyethylene.',
          },
          {
            question: 'Does Kylie Matte Liquid Lipstick contain plastic?',
            answer:
              'No solid polymer particles. Its published ingredient list is isododecane, mica, dicalcium phosphate, octyldodecanol, hydrogenated styrene/isoprene copolymer, stearalkonium hectorite, alcohol, flavour, water, an antioxidant, tin oxide and colorants. The hydrogenated styrene/isoprene copolymer is a synthetic elastomeric binder rather than a discrete solid particle, so it falls outside the EU definition of a synthetic polymer microparticle.',
          },
          {
            question: 'What are the ten Kylie Cosmetics badges?',
            answer:
              'As displayed on kyliecosmetics.com on 30 July 2026: vegan, cruelty free, clean, talc free, gluten free, tear free, paraben free, fragrance free, sulfate free and hypoallergenic. There is no plastic-free or microplastic-free badge, and to be fair to the brand, essentially no one in the industry offers one, because no widely adopted certification covers synthetic polymer microparticles.',
          },
          {
            question: 'Is polyethylene in a lip liner bad?',
            answer:
              'It is the mildest form of this issue. Polyethylene in a pencil acts as a wax hardener at low concentration, bound in a wax matrix rather than supplied as free microbeads, and lip liner is used in milligram quantities. It still meets the EU definition and will need to change for the EU market by October 2035, but if you are deciding what to replace first, a loose setting powder built on nylon microspheres matters considerably more.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Kylie Cosmetics',
            year: 2026,
            title: 'Published product ingredient lists and on-site product claim badges (Matte Liquid Lipstick, King Kylie Matte Lip Kit, High Gloss Liner Duo, Power Plush Longwear Foundation), retrieved 30 July 2026',
            publisher: 'kyliecosmetics.com',
            url: 'https://kyliecosmetics.com/',
          },
          {
            authors: 'Coty Inc.',
            year: 2021,
            title: 'Coty announces the relaunch of Kylie Cosmetics with new and improved clean and vegan formulas',
            publisher: 'Coty Inc. / Business Wire, 1 July 2021',
            url: 'https://www.coty.com/news',
          },
          {
            authors: 'European Commission',
            year: 2023,
            title: 'Commission Regulation (EU) 2023/2055 amending Annex XVII to REACH as regards synthetic polymer microparticles',
            publisher: 'Official Journal of the European Union (EUR-Lex, CELEX 32023R2055)',
            url: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A32023R2055',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
